import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link } from '@reach/router';
import { styled, Portal } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import Box from '@material-ui/core/Box';
import CssBaseline from '@material-ui/core/CssBaseline';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';

import IconHome from '@material-ui/icons/Home';
import IconMenu from '@material-ui/icons/Menu';
import IconUsers from '@material-ui/icons/People';
import IconMessages from '@material-ui/icons/Message';
import IconSupport from '@material-ui/icons/HelpOutline';
import ReportIcon from '@material-ui/icons/ReportProblem';
import IconPhotos from '@material-ui/icons/Image';


import logoSrc from './logo.png';
import AdminUserActions from './AdminUserActions';

export const DRAWER_WIDTH = 240;

const AppFrameContext = React.createContext({
  titleRef: null
});

const ImgLogo = styled('img')({
  width: 120,
});


const AppFrame: React.FC = ({ children }) => {

  const ref = useRef(null);
  const [ refState, setRefState ] = useState(null);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    setRefState(ref.current)
  }, []);

  const [ mobileOpen, setMobileOpen ] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const closeDrawer = () => {
    setMobileOpen(false);
  }

  const drawer = (
    <div>
      <Toolbar sx={{ alignItems: 'center', justifyContent: 'center' }}>
        <ImgLogo src={logoSrc} alt="Kuku Admin" />
      </Toolbar>
      <Divider />
      <List>
        <ListItem button component={Link} to="/" onClick={closeDrawer}>
          <ListItemIcon>
            <IconHome />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItem>

        <ListItem button component={Link} to="/users" onClick={closeDrawer}>
          <ListItemIcon>
            <IconUsers />
          </ListItemIcon>
          <ListItemText primary="Users" />
        </ListItem>

        <ListItem button component={Link} to="/photos" onClick={closeDrawer}>
          <ListItemIcon>
            <IconPhotos />
          </ListItemIcon>
          <ListItemText primary="Photos" />
        </ListItem>

        <ListItem button component={Link} to="/chats" onClick={closeDrawer}>
          <ListItemIcon>
            <IconMessages />
          </ListItemIcon>
          <ListItemText primary="Chats" />
        </ListItem>

        <ListItem button component={Link} to="/support" onClick={closeDrawer}>
          <ListItemIcon>
            <IconSupport />
          </ListItemIcon>
          <ListItemText primary="Support" />
        </ListItem>

        <ListItem button component={Link} to="/reported-users" onClick={closeDrawer}>
          <ListItemIcon>
            <ReportIcon />
          </ListItemIcon>
          <ListItemText primary="Reported users" />
        </ListItem>
      </List>

    </div>
  );

  const container = window !== undefined ? () => window.document.body : undefined;

  return (
    <AppFrameContext.Provider value={{ titleRef: refState }}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
            ml: { sm: `${DRAWER_WIDTH}px` },
          }}
          elevation={1}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <IconMenu />
            </IconButton>
            <Typography variant="h6" noWrap component="div" ref={ref} sx={{ flexGrow: 1 }}>
            </Typography>
            <AdminUserActions />
          </Toolbar>
        </AppBar>
        <Box
          component="nav"
          sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
          aria-label="mailbox folders"
        >
          <Drawer
            container={container}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
        <Box component="main" sx={{ flexGrow: 1, minHeight: '100vh' }}>
          <Toolbar />
          {children}
        </Box>
      </Box>
    </AppFrameContext.Provider>
  );
}

export default AppFrame;


interface AppFrameTitleProps {
  title: string;
}


export const AppFrameTitle: React.FC<AppFrameTitleProps> = ({ title }) => {
  const context = useContext(AppFrameContext);
  return (
    <Portal container={context.titleRef}>
      {title}
    </Portal>
  )
}